import {
  IncentivePlanMerchantEligibility,
  IncentivePlanUserTargeting,
} from 'lib/generated/api'

export const scheduleTypes = [
  'none',
  'daily',
  'weekly',
  'monthly',
  'yearly',
  'custom',
] as const
export type ScheduleType = typeof scheduleTypes[number]

export const scheduleFrequencies = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const
export type ScheduleFrequency = typeof scheduleFrequencies[number]

export const scheduleOccurrences = [
  'first',
  'second',
  'third',
  'fourth',
  'last',
] as const
export type ScheduleOccurrence = typeof scheduleOccurrences[number]

export const scheduleCustomOccurrences = [
  'sameMonthDay',
  'sameWeekDay',
] as const
export type ScheduleCustomOccurrence = typeof scheduleCustomOccurrences[number]

export const scheduleEndings = ['never', 'date'] as const
export type ScheduleEnding = typeof scheduleEndings[number]

export interface IncentivePlanSchedule {
  type: ScheduleType
  start: Date
  timezone: string
  fromTime: string
  toTime: string
  interval: number
  customFrequency: ScheduleFrequency
  days: number[]
  weekStart: number
  weekRange: number
  monthlyOccurrence: ScheduleOccurrence
  customOccurrence: ScheduleCustomOccurrence
  ending: ScheduleEnding
  endingOn: Date
}

export const incentiveTypes = [
  'creditOneTime',
  'creditRecurring',
  'discountAmount',
  'discountPercentage',
] as const
export type IncentiveType = typeof incentiveTypes[number]

export const merchantParticipationTypes = [
  'all_merchants',
  'workspace',
  'radius',
  'merchant_list',
] as const
export type MerchantParticipationType = typeof merchantParticipationTypes[number]

export const merchantRadiusUnits = ['kilometer', 'mile'] as const
export type MerchantRadiusUnit = typeof merchantRadiusUnits[number]

export const incentivePlanStatus = [
  'draft',
  'upcoming',
  'live',
  'paused',
  'ended',
] as const
export type IncentivePlanStatus = typeof incentivePlanStatus[number]

export interface IncentiveDetails {
  name?: string
  description?: string
  type?: IncentiveType
  currencyCode?: string
  creditAmount?: string
  discountAmount?: string
  discountPercentage?: number
  discountValueMax?: string
  orderValueMin?: string
  schedule?: IncentivePlanSchedule
}

export interface MerchantParticipation {
  original?: IncentivePlanMerchantEligibility
  type: MerchantParticipationType
  merchantList?: string
  merchantRadius?: number
  merchantRadiusUnit?: MerchantRadiusUnit
}

export interface IncentiveRecipients {
  original?: IncentivePlanUserTargeting
  allCompanies: boolean
  companies: string[]
  allPeople: boolean
  people: string[]
}

export interface IncentivePlan {
  id: string
  status: IncentivePlanStatus
  hasChangesDrafted: boolean
  canLaunch: boolean
  canRelaunch: boolean
  canPause: boolean
  canResume: boolean
  canDelete: boolean
  incentiveDetails: IncentiveDetails
  merchantParticipation: MerchantParticipation
  recipients: IncentiveRecipients
  notification?: never
}
